import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Eye, EyeOff, Loader2, RotateCw, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { klarysUrl } from './klarysApi';
import { getToken } from '../../services/tokenService';

const STATUT = {
  envoye: ['Envoyé', 'bg-emerald-100 text-emerald-800'],
  echec: ['Échec', 'bg-rose-100 text-rose-800'],
  classe: ['Classé (non envoyé)', 'bg-slate-100 text-slate-700'],
};
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const auth = () => ({ Authorization: `Bearer ${getToken()}` });

const LigneEnvoi = ({ e, i, membreId, onMaj }) => {
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATUT[e.statut] || STATUT.classe;
  const telecharger = async () => {
    try {
      const r = await axios.get(klarysUrl(`/membres/${membreId}/envois/${e.ged_id}/fichier`), { headers: auth(), responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = e.document || 'document';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Téléchargement impossible'); }
  };
  const reessayer = async () => {
    setBusy(true);
    try {
      await axios.post(klarysUrl(`/membres/${membreId}/envois/${e.ged_id}/reessayer`), {}, { headers: auth() });
      toast.success('Document renvoyé');
    } catch (err) { toast.error(err.response?.data?.detail || 'Nouvel échec de l\'envoi'); }
    setBusy(false);
    onMaj();
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`envois-klarys-ligne-${i}`}>
      <td className="py-2 pr-2 font-medium text-slate-800" data-testid={`envois-klarys-doc-${i}`}>{e.document}<span className="block text-[10px] font-normal text-slate-400">{e.sous_dossier}</span></td>
      <td className="py-2 pr-2 text-slate-600">{fmt(e.dernier_envoi?.at || e.classe_le)}</td>
      <td className="py-2 pr-2 text-slate-600">{e.dernier_envoi?.email || '—'}<span className="block text-[10px] text-slate-400">{e.dernier_envoi?.objet}</span></td>
      <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`envois-klarys-statut-${i}`}>{label}</span>
        {e.historique?.length > 1 && <span className="block text-[10px] text-slate-400">{e.historique.length} tentatives</span>}</td>
      <td className="py-2 pr-2 text-slate-600" data-testid={`envois-klarys-lecture-${i}`}>
        {e.statut !== 'envoye' ? '—' : e.dernier_envoi?.premiere_ouverture ? (
          <span className="flex items-center gap-1 text-emerald-700" title={`Dernière ouverture : ${fmt(e.dernier_envoi.derniere_ouverture)}`}>
            <Eye className="h-3.5 w-3.5" /> Ouvert le {fmt(e.dernier_envoi.premiere_ouverture)}{` (${e.dernier_envoi.ouvertures || 1}×)`}
          </span>
        ) : <span className="flex items-center gap-1 text-slate-400"><EyeOff className="h-3.5 w-3.5" /> Non ouvert</span>}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={telecharger} data-testid={`envois-klarys-telecharger-${i}`}><Download className="h-3.5 w-3.5" /></Button>
        {e.statut === 'echec' && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={reessayer} data-testid={`envois-klarys-reessayer-${i}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5 mr-1" />} Réessayer
          </Button>
        )}
      </td>
    </tr>
  );
};

export const EnvoisKlarysSection = ({ membreId }) => {
  const [items, setItems] = useState(null);
  const charger = useCallback(() => {
    if (!membreId) return;
    axios.get(klarysUrl(`/membres/${membreId}/envois`), { headers: auth() })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [membreId]);
  useEffect(() => { charger(); }, [charger]);
  if (!membreId) return null;
  return (
    <div className="rounded-xl border border-sky-100 overflow-hidden" data-testid="envois-klarys-section">
      <div className="px-4 py-2.5 bg-sky-50 border-b border-sky-100 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-sky-700" />
        <span className="text-xs font-bold text-sky-800 uppercase tracking-wider">Envois Klarys</span>
        {items && <span className="ml-auto text-[11px] text-sky-700" data-testid="envois-klarys-total">{items.length} document(s)</span>}
      </div>
      <div className="p-3">
        {items === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : items.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="envois-klarys-vide">Aucun document envoyé ou classé par Klarys pour ce membre.</p>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="pb-1 font-semibold">Document</th><th className="pb-1 font-semibold">Date</th><th className="pb-1 font-semibold">Destinataire / objet</th>
              <th className="pb-1 font-semibold">Statut</th><th className="pb-1 font-semibold">Lecture</th><th />
            </tr></thead>
            <tbody>{items.map((e, i) => <LigneEnvoi key={e.ged_id} e={e} i={i} membreId={membreId} onMaj={charger} />)}</tbody>
          </table>
        )}
        {items?.length > 0 && <p className="mt-2 text-[10px] text-slate-400">Lecture indicative : certaines messageries bloquent ou préchargent les images.</p>}
      </div>
    </div>
  );
};
